import { phoneNumberValidation } from '@/common/validation';
import { AddressCheck } from '@/components/address-check';
import { Form } from '@/components/forms';
import { DwellingInput } from '@/components/forms/dwelling-input';
import { EmailInput } from '@/components/forms/email-input/component';
import { GenderInput } from '@/components/forms/gender-input';
import { AddressField } from '@/components/inputs';
import { Checkbox, Flex, NumberInput, Text, TextInput } from '@mantine/core';
import { FormikProps } from 'formik';
import { useCallback } from 'react';
import * as yup from 'yup';
import styles from './styles.module.css';

const validationSchema = yup.object({
	firstName: yup.string().required('First name is required').max(40, 'First name is too long'), // 40 chars Salesforce limitation
	lastName: yup.string().required('Last name is required').max(40, 'Last name is too long'), // 40 chars Salesforce limitation
	email: yup
		.string()
		.email('Invalid email address')
		.required('Email is required')
		.max(80, 'Email is too long'), // Salesforce limitation
	formattedAddress: yup.string().required('Please enter your address.'),
	postcode: yup.string().required('Please enter your postcode.'),
	phoneNumber: phoneNumberValidation,
	gender: yup.string().nullable(),
	dwellingType: yup.string().nullable(),
	householdSize: yup.number().nullable().moreThan(0, 'Please enter your household size.'),
	isParticipatingInPrizeDraw: yup.boolean().nullable().default(false),
	isOver18: yup.boolean().when('isParticipatingInPrizeDraw', {
		is: true,
		then: (schema) => schema.isTrue('You must be over 18 to participate in the prize draw.'),
		otherwise: (schema) => schema.notRequired(),
	}),
});

export type AccountFormValues = {
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber?: string | null | undefined;
	formattedAddress?: string | null | undefined;
	addressLine1?: string | null | undefined;
	addressLine2?: string | null | undefined;
	city?: string | null | undefined;
	cityPlaceId?: string | null | undefined;
	council?: string | null | undefined;
	postcode?: string | null | undefined;
	state?: string | null | undefined;
	gender?: string | null | undefined;
	dwellingType?: string | null | undefined;
	householdSize?: number | null | undefined;
	isOver18?: boolean | null | undefined;
	isParticipatingInPrizeDraw?: boolean | null | undefined;
	isManualAddress?: boolean | null | undefined;
	lat?: number | null | undefined;
	lng?: number | null | undefined;
	placeId?: string | null | undefined;
};

type AccountFormProps = {
	onSubmit: (values: AccountFormValues) => void;
	formRef: React.RefObject<FormikProps<AccountFormValues>>;
	initialValues: AccountFormValues;
};

const textInputLabelClassNames = {
	label: styles.inputLabel,
};

export const AccountForm = ({ formRef, onSubmit, initialValues }: AccountFormProps) => {
	return (
		<Form
			initialValues={initialValues}
			onSubmit={onSubmit}
			innerRef={formRef}
			validationSchema={validationSchema}
		>
			{(formikProps) => {
				return <FormFields {...formikProps} />;
			}}
		</Form>
	);
};

const FormFields = ({
	values,
	setFieldValue,
	getFieldProps,
	touched,
	errors,
}: FormikProps<AccountFormValues>) => {
	const AddressChecker = useCallback(() => {
		if (!values.lat || !values.lng) {
			return null;
		}

		return (
			<AddressCheck
				council={values.council}
				postcode={values.postcode}
				state={values.state}
				lat={values.lat}
				lng={values.lng}
			/>
		);
	}, [values.council, values.postcode, values.state, values.lat, values.lng]);

	return (
		<>
			<Text fz="24px" fw={900}>
				Basic Details
			</Text>
			<Flex gap="md" w="100%">
				<TextInput
					label="First Name"
					placeholder="First Name"
					className={styles.cell}
					classNames={textInputLabelClassNames}
					{...getFieldProps('firstName')}
					error={touched.firstName && errors.firstName}
				/>
				<TextInput
					label="Last Name"
					placeholder="Last Name"
					className={styles.cell}
					classNames={textInputLabelClassNames}
					{...getFieldProps('lastName')}
					error={touched.lastName && errors.lastName}
				/>
			</Flex>
			<EmailInput
				label="Email"
				placeholder="Email"
				classNames={textInputLabelClassNames}
				{...getFieldProps('email')}
				error={touched.email && errors.email}
			/>
			<Text fz="24px" fw={900}>
				Address
			</Text>

			<AddressField
				label="Enter Your Address"
				value={values.formattedAddress}
				error={touched.formattedAddress ? errors.formattedAddress : undefined}
			/>

			<AddressChecker />
			<Text fz="24px" fw={900}>
				Profile (Optional)
			</Text>
			<TextInput
				label="Phone Number"
				placeholder=""
				type="tel"
				classNames={textInputLabelClassNames}
				{...getFieldProps('phoneNumber')}
				error={touched.phoneNumber && errors.phoneNumber}
			/>
			<GenderInput
				label="Gender"
				value={values.gender}
				onChange={(e) => setFieldValue('gender', e)}
				error={touched.gender && errors.gender}
			/>
			<Flex gap="md">
				<DwellingInput
					flex={1}
					label="Dwelling Type"
					value={values.dwellingType}
					onChange={(e) => setFieldValue('dwellingType', e)}
					error={touched.dwellingType && errors.dwellingType}
				/>
				<NumberInput
					flex={1}
					label="Household Size"
					placeholder=""
					value={values.householdSize ?? undefined}
					onChange={(e) => setFieldValue('householdSize', e === '' ? undefined : e)}
					error={touched.householdSize && errors.householdSize}
				/>
			</Flex>
			<Checkbox
				label="I wish to participate in the prize draw."
				checked={values.isParticipatingInPrizeDraw ?? false}
				onChange={(e) => setFieldValue('isParticipatingInPrizeDraw', e.target.checked)}
				error={touched.isParticipatingInPrizeDraw && errors.isParticipatingInPrizeDraw}
			/>
			<Checkbox
				label="I am over 18 years of age."
				checked={values.isOver18 ?? false}
				onChange={(e) => setFieldValue('isOver18', e.target.checked)}
				error={touched.isOver18 && errors.isOver18}
			/>
		</>
	);
};
